import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import CommentItem from './CommentItem';
import commentsService from '../../api/comments';
import { useAuth } from '../../hooks/useAuth';
import AuthModal from '../auth/AuthModal';
import { useUniversity } from '../../contexts/UniversityContext';
import { useWebSocket } from '../../hooks/useWebSocket';
import SkeletonLoader from '../common/SkeletonLoader';
import ReactDOM from 'react-dom';

const Container = styled.div`
  padding: 10px 0 0 10px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 992px) {
    padding: 8px 0 0 8px;
  }
  
  @media (max-width: 768px) {
    padding: 6px 0 0 6px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 0 0 4px;
  }
  
  @media (max-width: 360px) {
    padding: 4px 0 0 2px;
  }
`;

const CommentForm = styled.form`
  display: flex;
  margin-bottom: 12px;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  gap: 8px;
  
  @media (max-width: 768px) {
    margin-bottom: 10px;
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 8px;
    gap: 5px;
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(45deg, #1877f2, #4267B2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 5px;
  flex-shrink: 0;
  font-size: 16px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  
  @media (max-width: 768px) {
    width: 30px;
    height: 30px;
    font-size: 15px;
    margin-right: 4px;
  }
  
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 14px;
    margin-right: 3px;
  }
`;

const CommentInput = styled.input`
  flex-grow: 1;
  border: 1px solid ${props => props.theme.divider};
  border-radius: 20px;
  padding: 8px 14px;
  outline: none;
  font-size: 14px;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.text};
  transition: all 0.2s ease;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  
  @media (max-width: 768px) {
    padding: 7px 12px;
    font-size: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 10px;
    font-size: 16px;
    border-radius: 16px;
  }
  
  &:focus {
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
    font-size: 14px;
    
    @media (max-width: 768px) {
      font-size: 16px;
    }
    
    @media (max-width: 480px) {
      font-size: 16px;
    }
  }
  
  ${props => !props.$isAuthenticated && `
    background-color: ${props.theme.buttonLight};
    cursor: pointer;
    pointer-events: all;
  `}
  
  ${props => props.$disabled && `
    background-color: ${props.theme.buttonLight};
    opacity: 0.7;
    cursor: not-allowed;
  `}
`;

const CommentsList = styled.div`
  margin-top: 10px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    margin-top: 8px;
  }
  
  @media (max-width: 480px) {
    margin-top: 6px;
  }
  
  @media (max-width: 360px) {
    margin-top: 4px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 15px;
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
  background-color: ${props => props.theme.cardBg};
  border-radius: 12px;
  margin: 10px 0;
  border: 1px dashed ${props => props.theme.divider};
  word-break: break-word;
  
  @media (max-width: 480px) {
    padding: 12px;
    font-size: 13px;
  }
`;

const SendButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 20px;
  padding: 0 5px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  transition: all 0.2s;
  
  &:hover {
    transform: ${props => props.$disabled ? 'none' : 'scale(1.1)'};
    color: ${props => {
      if (props.$disabled) return props.theme.textSecondary;
      return props.$hasText ? props.theme.primary : props.theme.textSecondary;
    }};
  }
  
  opacity: ${props => {
    if (props.$disabled) return 0.5;
    return props.$hasText ? 1 : 0.5;
  }};
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-left: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
    margin-left: 2px;
  }
  
  display: ${props => props.$isAuthenticated ? 'flex' : 'none'};
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 14px;
  margin: 0 0 10px 0;
  padding: 8px;
  background-color: rgba(231, 76, 60, 0.1);
  border-radius: 8px;
  text-align: center;
  width: 100%;
  word-break: break-word;
  
  @media (max-width: 768px) {
    font-size: 13px;
    padding: 6px;
    margin-bottom: 8px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    padding: 5px;
    margin-bottom: 6px;
  }
`;

// Special styled component for rate limiting errors
const RateLimitError = styled(ErrorMessage)`
  background-color: rgba(241, 196, 15, 0.1);
  color: #d35400;
  border-left: 3px solid #f39c12;
  padding-left: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  span.icon {
    font-size: 16px;
    margin-bottom: 4px;
  }
  
  span.message {
    font-weight: 500;
  }
  
  @media (max-width: 480px) {
    span.icon {
      font-size: 14px;
    }
  }
`;

// Loader para reflejar que se está cargando más contenido
const LoadingContainer = styled.div`
  padding: 10px;
  text-align: center;
  color: ${props => props.theme.textSecondary};
`;

// Componente para la observación infinita
const ObserverTarget = styled.div`
  width: 100%;
  height: 10px;
  margin: 10px 0;
`;

// Reutilizar las constantes de longitud de comentarios
const MAX_COMMENT_LENGTH = 200;
const MIN_COMMENT_LENGTH = 1;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  width: 100%;
  min-height: 100px;

  &:after {
    content: "";
    width: 32px;
    height: 32px;
    border: 2px solid ${props => props.theme.divider};
    border-top: 2px solid ${props => props.theme.primary};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @media (max-width: 768px) {
    &:after {
      width: 24px;
      height: 24px;
    }
  }
`;

const CommentList = ({ messageId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  const observerRef = useRef(null);
  const observerTargetRef = useRef(null);
  
  const { isAuthenticated, user } = useAuth();
  const { currentUniversity } = useUniversity();
  
  // Definir MAX_REPLY_LEVEL para limitar el anidamiento
  const MAX_REPLY_LEVEL = 3; // Permitir niveles 0, 1 y 2
  
  // Add WebSocket hook
  const { addMessageHandler } = useWebSocket();
  
  // Agregar hook para manejar el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Función para obtener el placeholder según el ancho de la ventana
  const getPlaceholder = useCallback(() => {
    if (isAuthenticated) return "Escribe un comentario...";
    if (windowWidth <= 360) return "Inicia sesión...";
    if (windowWidth <= 480) return "Inicia sesión para comentar";
    return "Inicia sesión para comentar...";
  }, [isAuthenticated, windowWidth]);
  
  // Función para organizar los comentarios en una estructura jerárquica
  const organizeCommentsHierarchy = (flatComments) => {
    // Crear un mapa para acceder rápidamente a los comentarios por ID
    const commentMap = {};
    const rootComments = [];
    
    // Primera pasada: mapear todos los comentarios por ID
    flatComments.forEach(comment => {
      // Crear una copia limpia del comentario
      const cleanComment = { ...comment };
      
      // Siempre eliminar la propiedad "replies" si es un número (contador)
      if (typeof cleanComment.replies === 'number') {
        delete cleanComment.replies;
      }
      
      // Asegurarse de que cada comentario tenga un array de respuestas
      cleanComment.replies = [];
      
      // IMPORTANTE: Preservar el nivel que viene desde la base de datos si existe
      if (typeof cleanComment.level !== 'number') {
        cleanComment.level = 0;
      }
      
      commentMap[comment.id] = cleanComment;
    });
    
    // Segunda pasada: organizar en estructura jerárquica
    flatComments.forEach(comment => {
      // Si tiene parent_id y ese padre existe en nuestro mapa
      if (comment.parent_id && commentMap[comment.parent_id]) {
        // Añadir como respuesta al comentario padre
        commentMap[comment.parent_id].replies.push(commentMap[comment.id]);
        
        // IMPORTANTE: Asegurar que el nivel siempre sea correcto basado en la jerarquía,
        // independientemente del valor de la base de datos
        commentMap[comment.id].level = commentMap[comment.parent_id].level + 1;
      } else {
        // Es un comentario raíz
        rootComments.push(commentMap[comment.id]);
        // Asegurar que los comentarios raíz siempre tengan nivel 0
        commentMap[comment.id].level = 0;
      }
    });
    
    // Ordenar los comentarios raíz por fecha (más recientes primero)
    rootComments.sort((a, b) => {
      // Asegurarse de que ambas fechas sean válidas
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      
      // Comparación segura
      return dateB - dateA;
    });
    
    // Ordenar recursivamente las respuestas por fecha
    const sortReplies = (comments) => {
      for (const comment of comments) {
        if (comment.replies && comment.replies.length > 0) {
          comment.replies.sort((a, b) => {
            // Asegurarse de que ambas fechas sean válidas
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            
            // Comparación segura
            return dateB - dateA;
          });
          sortReplies(comment.replies);
        }
      }
    };
    
    sortReplies(rootComments);
    return rootComments;
  };
  
  // Cargar comentarios
  const loadComments = useCallback(async (pageNum) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      
      const response = await commentsService.getComments(messageId, pageNum);
      
      // Asegurarse de que response es un array
      const commentsArray = Array.isArray(response) ? response : [];
      
      // Organizar los comentarios en jerarquía
      const organizedComments = organizeCommentsHierarchy(commentsArray);
      
      if (pageNum === 1) {
        setComments(organizedComments);
      } else {
        // Para paginación, necesitamos combinar con los comentarios existentes
        setComments(prev => {
          // Crear una copia profunda de los comentarios previos para evitar mutaciones
          const updatedComments = JSON.parse(JSON.stringify(prev));
          const existingCommentIds = new Set();
          
          // Función para recopilar todos los IDs existentes (incluyendo respuestas)
          const collectIds = (comments) => {
            comments.forEach(comment => {
              existingCommentIds.add(comment.id);
              if (comment.replies && comment.replies.length > 0) {
                collectIds(comment.replies);
              }
            });
          };
          
          // Recopilar todos los IDs existentes
          collectIds(updatedComments);
          
          // Función para buscar un padre e insertar la respuesta
          const findParentAndAddReply = (comments, reply) => {
            for (let i = 0; i < comments.length; i++) {
              if (comments[i].id === reply.parent_id) {
                if (!comments[i].replies) {
                  comments[i].replies = [];
                }
                // Asegurarse de que no sea un duplicado
                if (!comments[i].replies.some(r => r.id === reply.id)) {
                  reply.level = (comments[i].level || 0) + 1;
                  comments[i].replies.push(reply);
                }
                return true;
              }
              
              // Buscar recursivamente en las respuestas
              if (comments[i].replies && comments[i].replies.length > 0) {
                if (findParentAndAddReply(comments[i].replies, reply)) {
                  return true;
                }
              }
            }
            return false;
          };
          
          // Primero procesamos los nuevos comentarios raíz
          organizedComments.forEach(newComment => {
            // Si es un comentario raíz y no existe, agregarlo
            if (!newComment.parent_id && !existingCommentIds.has(newComment.id)) {
              updatedComments.push(newComment);
              existingCommentIds.add(newComment.id);
            }
          });
          
          // Luego procesamos las respuestas (comentarios con parent_id)
          const replies = [];
          organizedComments.forEach(comment => {
            // Buscar todas las respuestas de forma plana
            const collectReplies = (comm) => {
              if (comm.parent_id) replies.push(comm);
              if (comm.replies && comm.replies.length > 0) {
                comm.replies.forEach(collectReplies);
              }
            };
            collectReplies(comment);
          });
          
          // Ahora, agregamos las respuestas si sus padres existen
          replies.forEach(reply => {
            if (!existingCommentIds.has(reply.id)) {
              // Intentar encontrar el padre y agregar la respuesta
              const parentFound = findParentAndAddReply(updatedComments, reply);
              
              // Si no se encuentra el padre pero está en los nuevos comentarios, 
              // podría ser una respuesta a otro comentario nuevo
              if (!parentFound) {
                // Si no podemos encontrar el padre, lo agregamos como comentario raíz
                // pero mantenemos el nivel que ya tiene para preservar el formato visual
                reply.replies = reply.replies || [];
                updatedComments.push(reply);
                existingCommentIds.add(reply.id);
              }
            }
          });
          
          // Reordenar comentarios raíz por fecha
          return updatedComments.sort((a, b) => {
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateB - dateA;
          });
        });
      }
      
      setHasMore(commentsArray.length === 10); // Asumiendo que el límite es 10
    } catch (error) {
      console.error('Error al cargar comentarios:', error);
      setError('No se pudieron cargar los comentarios');
      if (pageNum === 1) {
        setComments([]);
      }
    } finally {
      if (pageNum === 1) {
        setLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  }, [messageId]);
  
  useEffect(() => {
    // Resetear estados cuando cambia el mensaje
    setPage(1);
    setComments([]);
    setHasMore(true);
    loadComments(1);
  }, [messageId, loadComments]);
  
  // Configurar el observador de intersección para carga infinita
  useEffect(() => {
    const options = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.1
    };
    
    const handleObserver = (entries) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !isLoadingMore && !loading) {
        setPage(prevPage => prevPage + 1);
      }
    };
    
    observerRef.current = new IntersectionObserver(handleObserver, options);
    
    if (observerTargetRef.current) {
      observerRef.current.observe(observerTargetRef.current);
    }
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoadingMore, loading]);
  
  // Cargar más comentarios cuando cambia la página
  useEffect(() => {
    if (page > 1) {
      loadComments(page);
    }
  }, [page, loadComments]);
  
  // Add WebSocket listener for comment updates
  useEffect(() => {
    const handleWebSocketComment = (data) => {
      // Handle comment-related WebSocket messages
      if (data.type === 'comment' && data.messageId === messageId) {
        // Invalidate comment cache for this message
        commentsService.invalidateMessageComments(messageId);
        
        // Reload first page of comments to get the latest
        loadComments(1);
      }
    };
    
    // Register handler
    const unsubscribe = addMessageHandler(handleWebSocketComment);
    
    return () => {
      unsubscribe();
    };
  }, [addMessageHandler, messageId, loadComments]);
  
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    const trimmedComment = commentText.trim();
    
    // Validar longitud mínima
    if (trimmedComment.length < MIN_COMMENT_LENGTH) {
      setError('El comentario no puede estar vacío');
      return;
    }
    
    // Validar longitud máxima (doble verificación)
    if (trimmedComment.length > MAX_COMMENT_LENGTH) {
      setError(`El comentario no puede exceder los ${MAX_COMMENT_LENGTH} caracteres`);
      return;
    }
    
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    
    // Verificar si el usuario pertenece a la universidad seleccionada
    if (user && user.university !== currentUniversity?.id) {
      setError(`Solo puedes comentar en tu universidad: ${user.university.toUpperCase()}`);
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    try {
      setIsSubmittingComment(true);
      const newComment = await commentsService.addComment(messageId, trimmedComment);
      
      // Cache will be invalidated inside the addComment method
      
      // Procesar el comentario para asegurar que replies sea un array y no un número
      if (typeof newComment.replies === 'number') {
        delete newComment.replies;
      }
      newComment.replies = [];
      
      // Actualizar la lista de comentarios localmente
      setComments(prevComments => {
        // Clonar los comentarios actuales
        let newComments = [...prevComments];
        
        // Si el comentario no tiene padre, agregarlo al principio
        if (!newComment.parent_id || newComment.parent_id.toString() === '000000000000000000000000') {
          return [newComment, ...newComments];
        } else {
          // Buscar recursivamente el comentario padre y actualizar sus respuestas
          const findParentAndAddReply = (comments, reply) => {
            for (let i = 0; i < comments.length; i++) {
              if (comments[i].id === reply.parent_id.toString()) {
                // Encontrado el padre, agregar como respuesta
                if (!comments[i].replies) {
                  comments[i].replies = [];
                }
                comments[i].replies.unshift(reply);
                return true;
              }
              
              // Buscar en las respuestas si existen
              if (comments[i].replies && comments[i].replies.length > 0) {
                const found = findParentAndAddReply(comments[i].replies, reply);
                if (found) return true;
              }
            }
            return false;
          };
          
          findParentAndAddReply(newComments, newComment);
          return newComments;
        }
      });
      
      // Limpiar el campo de comentario
      setCommentText('');
      setError('');
      
    } catch (err) {
      console.error('Error al enviar comentario:', err);
      
      // Handle rate limiting errors specially
      if (err.message && (
          err.message.includes('excedido el límite') || 
          err.message.includes('demasiado rápido')
      )) {
        setError(err.message);
        // Keep the error visible a bit longer for rate limit issues
        setTimeout(() => setError(null), 5000);
      } else if (err.response && err.response.status === 429) {
        // Handle server rate limit response
        setError(err.response.data || 'Has alcanzado el límite de comentarios. Inténtalo más tarde.');
        setTimeout(() => setError(null), 5000);
      } else {
        setError('Error al enviar el comentario. Intenta de nuevo.');
        setTimeout(() => setError(null), 3000);
      }
    } finally {
      setIsSubmittingComment(false);
    }
  };
  
  const handleReply = async (parentId, text) => {
    if (!text.trim() || !isAuthenticated) return;
    
    // Verificar si el usuario pertenece a la universidad seleccionada
    if (user && user.university !== currentUniversity?.id) {
      setError(`Solo puedes comentar en tu universidad: ${user.university.toUpperCase()}`);
      setTimeout(() => setError(null), 3000);
      return Promise.reject(new Error('Universidad incorrecta'));
    }
    
    try {
      // Validate message ID
      if (!messageId || typeof messageId !== 'string' || messageId.length < 5) {
        setError('ID de mensaje inválido');
        return Promise.reject(new Error('ID de mensaje inválido'));
      }
      
      // Función para encontrar nivel del comentario padre
      const findParentLevel = (comments, id) => {
        for (const comment of comments) {
          if (comment.id === id) {
            return comment.level || 0;
          }
          if (comment.replies && comment.replies.length > 0) {
            const foundLevel = findParentLevel(comment.replies, id);
            if (foundLevel !== -1) {
              return foundLevel;
            }
          }
        }
        return -1; // No encontrado
      };
      
      // Verificar si el padre está en el nivel máximo
      const parentLevel = findParentLevel(comments, parentId);
      // Permitir respuestas hasta nivel 2 (0→1→2)
      if (parentLevel >= MAX_REPLY_LEVEL - 1) {
        setError('No se pueden añadir más respuestas a este nivel');
        setTimeout(() => setError(null), 3000);
        return Promise.reject(new Error('Máximo nivel de anidamiento alcanzado'));
      }
      
      const newComment = await commentsService.addComment(messageId, text, parentId);
      
      // Cache will be invalidated inside the addComment method
      
      // Asegurarnos de que los contadores estén correctamente establecidos
      if (typeof newComment.replies === 'number') {
        delete newComment.replies;
      }
      
      // Inicializar replies como un array vacío
      newComment.replies = [];
      
      // Establecer el nivel correcto explícitamente
      newComment.level = parentLevel + 1;
      
      // Actualizar el árbol de comentarios manteniendo la estructura jerárquica
      setComments(prev => {
        // Crear una copia profunda para evitar mutaciones
        const updatedComments = JSON.parse(JSON.stringify(prev));
        
        // Función recursiva para encontrar y actualizar el comentario padre
        const updateReplies = (comments) => {
          for (let i = 0; i < comments.length; i++) {
            if (comments[i].id === parentId) {
              if (!comments[i].replies) {
                comments[i].replies = [];
              }
              // Asegurarse de que el nuevo comentario tenga un array de respuestas vacío
              comments[i].replies.unshift(newComment);
              return true;
            }
            if (comments[i].replies && updateReplies(comments[i].replies)) {
              return true;
            }
          }
          return false;
        };
        
        // Intentar encontrar el padre y añadir la respuesta
        if (!updateReplies(updatedComments)) {
          // Si no se encuentra el padre, añadir al principio como comentario raíz
          newComment.level = 0; // Es un comentario raíz
          updatedComments.unshift(newComment);
        }
        
        return updatedComments;
      });
      
      return Promise.resolve(newComment);
    } catch (error) {
      console.error('Error al responder comentario:', error);
      
      // Handle rate limiting errors
      if (error.message && (
          error.message.includes('excedido el límite') || 
          error.message.includes('demasiado rápido')
      )) {
        setError(error.message);
        setTimeout(() => setError(null), 5000);
      } else if (error.response && error.response.status === 429) {
        // Handle server rate limit response
        setError(error.response.data || 'Has alcanzado el límite de comentarios. Inténtalo más tarde.');
        setTimeout(() => setError(null), 5000);
      } else {
        setError('Error al publicar la respuesta. Por favor intenta nuevamente.');
        setTimeout(() => setError(null), 3000);
      }
      
      return Promise.reject(error);
    }
  };
  
  const handleCommentClick = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
    }
  };
  
  const handleCommentChange = (e) => {
    // Si no está autenticado o no puede comentar, no procesar cambios
    if (!isAuthenticated || !canComment) {
      handleCommentClick();
      return;
    }
    
    // Validar longitud máxima mientras el usuario escribe
    if (e.target.value.length <= MAX_COMMENT_LENGTH) {
      setCommentText(e.target.value);
      setError('');
    }
  };
  
  const closeAuthModal = () => {
    setShowAuthModal(false);
  };
  
  // Verificar si el usuario puede comentar
  const canComment = isAuthenticated && (!user || user.university === currentUniversity?.id);
  
  return (
    <>
      <Container>
        {/* Mostrar mensaje de error si el usuario no pertenece a esta universidad */}
        {isAuthenticated && user && user.university !== currentUniversity?.id && (
          <ErrorMessage>
            Solo puedes comentar en tu universidad: {user.university.toUpperCase()}
          </ErrorMessage>
        )}
        
        {/* Show rate limit errors with special styling */}
        {error && (
          error.includes('excedido el límite') || error.includes('demasiado rápido') || error.includes('alcanzado el límite') ? (
            <RateLimitError>
              <span className="icon">⏱️</span>
              <span className="message">{error}</span>
            </RateLimitError>
          ) : (
            <ErrorMessage>{error}</ErrorMessage>
          )
        )}
        
        <CommentForm onSubmit={handleCommentSubmit}>
          <UserAvatar>
            🐸
          </UserAvatar>
          <CommentInput
            type="text"
            placeholder={getPlaceholder()}
            value={commentText}
            onChange={handleCommentChange}
            onClick={handleCommentClick}
            $isAuthenticated={isAuthenticated}
            $disabled={!canComment || isSubmittingComment}
            disabled={!canComment || isSubmittingComment}
            id={`comment-input-${messageId}`}
            name={`comment-input-${messageId}`}
            readOnly={!isAuthenticated || (user && user.university !== currentUniversity?.id)}
          />
          {!isSubmittingComment && commentText.length > MAX_COMMENT_LENGTH * 0.7 && (
            <CharacterCounter isNearLimit={commentText.length > MAX_COMMENT_LENGTH * 0.9}>
              {commentText.length}/{MAX_COMMENT_LENGTH}
            </CharacterCounter>
          )}
          <SendButton 
            type="submit" 
            $hasText={commentText.trim().length > 0}
            $isAuthenticated={isAuthenticated}
            $disabled={!canComment || isSubmittingComment}
            disabled={!canComment || isSubmittingComment || commentText.trim().length < MIN_COMMENT_LENGTH}
            title="Enviar comentario"
          >
            {isSubmittingComment ? (
              <span>...</span>
            ) : (
              <span role="img" aria-label="enviar">➤</span>
            )}
          </SendButton>
        </CommentForm>
        
        {isSubmittingComment && (
          <div style={{ fontSize: '12px', margin: '-10px 0 8px 42px', color: '#1877f2' }}>
            Comentando...
          </div>
        )}
        
        <CommentsList>
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <EmptyState>{error}</EmptyState>
          ) : comments && comments.length > 0 ? (
            <>
              {comments.map(comment => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  onReply={handleReply}
                  onAuthRequired={() => setShowAuthModal(true)}
                  level={comment.level || 0}
                />
              ))}
              
              {/* Punto de observación para carga infinita */}
              {hasMore && (
                <ObserverTarget ref={observerTargetRef} />
              )}
              
              {/* Indicador de carga para más comentarios */}
              {isLoadingMore && (
                <LoadingContainer>
                  <SkeletonLoader type="message" count={2} />
                </LoadingContainer>
              )}
            </>
          ) : (
            <EmptyState>
              <span role="img" aria-label="Sapito triste">🐸</span> No hay comentarios aún. Sé el primero en comentar.
            </EmptyState>
          )}
        </CommentsList>
      </Container>
      
      {/* Modal de Autenticación */}
      {showAuthModal && 
        ReactDOM.createPortal(
          <AuthModal show={showAuthModal} onClose={closeAuthModal} initialTab="login" />,
          document.body
        )
      }
    </>
  );
};

// Estilo para el contador de caracteres
const CharacterCounter = styled.div`
  text-align: right;
  font-size: 10px;
  margin-top: 2px;
  color: ${props => props.isNearLimit ? '#e74c3c' : '#777'};
  padding-right: 80px;
  word-break: break-word;
`;

export default CommentList;
